import {
  cloneElement,
  isValidElement,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { useAnchoredPosition, useOutsidePointer } from "../hooks";
import { activeElement, cx } from "../util";
import { GfPortal } from "./Portal";

export type MenuItem =
  | {
      type?: "item";
      key?: string;
      label: ReactNode;
      icon?: ReactNode;
      /** Right-aligned hint (shortcut, count). */
      hint?: ReactNode;
      onSelect: () => void;
      danger?: boolean;
      disabled?: boolean;
    }
  | { type: "separator"; key?: string }
  | { type: "label"; key?: string; label: ReactNode };

export interface MenuProps {
  /** The button that opens the menu (Button / IconButton / any button element). */
  trigger: ReactElement;
  items: ReadonlyArray<MenuItem | false | null | undefined>;
  align?: "start" | "end";
  /** Optional header inside the menu (e.g. signed-in user). */
  header?: ReactNode;
  minWidth?: number;
  className?: string;
  /** Accessible name for the menu. */
  label?: string;
}

type TriggerProps = {
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void;
  "aria-haspopup"?: "menu";
  "aria-expanded"?: boolean;
  "aria-controls"?: string;
};

/** Dropdown menu (WAI-ARIA menu button). Arrow keys, Home/End, Enter, Escape. */
export function Menu({ trigger, items, align = "end", header, minWidth = 200, className, label }: MenuProps) {
  const menuId = `gf-menu${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const list = items.filter((i): i is MenuItem => !!i);

  useAnchoredPosition(anchorRef, menuRef, open, { align, side: "bottom", offset: 6 });
  useOutsidePointer([anchorRef, menuRef], open, () => setOpen(false));

  function itemEls(): HTMLButtonElement[] {
    return Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("[role='menuitem']:not([disabled])") ?? []);
  }

  function focusTrigger() {
    anchorRef.current?.querySelector<HTMLElement>("button, a, [tabindex]")?.focus();
  }

  function openMenu(focus: "first" | "last") {
    setOpen(true);
    window.setTimeout(() => {
      const els = itemEls();
      (focus === "first" ? els[0] : els[els.length - 1])?.focus();
    }, 0);
  }

  function close(returnFocus: boolean) {
    setOpen(false);
    if (returnFocus) focusTrigger();
  }

  function onMenuKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const els = itemEls();
    const root = (menuRef.current?.getRootNode() ?? document) as Document | ShadowRoot;
    const idx = els.indexOf(activeElement(root) as HTMLButtonElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      els[(idx + 1) % els.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      els[(idx - 1 + els.length) % els.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      els[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      els[els.length - 1]?.focus();
    } else if (e.key === "Escape" || e.key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    }
  }

  const triggerEl = isValidElement<TriggerProps>(trigger)
    ? cloneElement(trigger, {
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": open ? menuId : undefined,
        onClick: (e: MouseEvent<HTMLElement>) => {
          trigger.props.onClick?.(e);
          if (open) close(false);
          else openMenu("first");
        },
        onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
          trigger.props.onKeyDown?.(e);
          if (e.key === "ArrowDown") {
            e.preventDefault();
            openMenu("first");
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            openMenu("last");
          }
        },
      })
    : trigger;

  return (
    <span ref={anchorRef} className="gf-menu-anchor">
      {triggerEl}
      {open ? (
        <GfPortal>
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={label}
            className={cx("gf-menu", className)}
            style={{ minWidth, top: -9999, left: -9999 }}
            onKeyDown={onMenuKeyDown}
          >
            {header ? <div className="gf-menu__header">{header}</div> : null}
            {list.map((item, i) => {
              if (item.type === "separator") return <div key={item.key ?? `sep-${i}`} role="separator" className="gf-menu__sep" />;
              if (item.type === "label")
                return (
                  <div key={item.key ?? `lbl-${i}`} className="gf-menu__label" role="presentation">
                    {item.label}
                  </div>
                );
              return (
                <button
                  key={item.key ?? `item-${i}`}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  disabled={item.disabled}
                  className={cx("gf-menu__item", item.danger && "gf-menu__item--danger")}
                  onClick={() => {
                    close(true);
                    item.onSelect();
                  }}
                >
                  {item.icon ? <span className="gf-menu__icon">{item.icon}</span> : null}
                  <span className="gf-menu__text">{item.label}</span>
                  {item.hint ? <span className="gf-menu__hint">{item.hint}</span> : null}
                </button>
              );
            })}
          </div>
        </GfPortal>
      ) : null}
    </span>
  );
}

/** Alias: some engineers reach for "Dropdown". Same component. */
export const Dropdown = Menu;
